import { formatApiErrorMessage } from '../services/api';

export type ReadAccessGrantStatus = 'ACTIVE' | 'REVOKED';

export interface DocumentReadAccessGrant {
  id: number;
  ownerUsername: string;
  viewerUsername?: string;
  documentName: string;
  status: ReadAccessGrantStatus;
  message?: string;
  createdAt: string;
  revokedAt?: string;
}

export interface OpenReadOnlyDocumentResponse {
  grantId: number;
  ownerUserId: number;
  ownerUsername: string;
  documentDbId: number;
  documentName: string;
  categoryId: string;
  accessMode: 'READ_ONLY';
}

export function filterActiveReadAccessGrants(
  grants: DocumentReadAccessGrant[],
): DocumentReadAccessGrant[] {
  return grants.filter((g) => g.status === 'ACTIVE');
}

export function readOnlyDocumentTabId(grantId: number): string {
  return `readonly-${grantId}`;
}

/** Tab title for a live read-only session (distinct from the viewer's own doc with the same name). */
export function readOnlyDocumentLabel(documentName: string, ownerUsername: string): string {
  return `${documentName} (${ownerUsername})`;
}

export function isReadOnlyDocumentTabId(tabId: string): boolean {
  return tabId.startsWith('readonly-');
}

/** Select value prefix for merging from a view-only shared document. */
export const MERGE_FROM_READ_ACCESS_GRANT_PREFIX = 'readonly-grant:';

export function mergeFromReadAccessGrantValue(grantId: number): string {
  return `${MERGE_FROM_READ_ACCESS_GRANT_PREFIX}${grantId}`;
}

export function parseMergeFromReadAccessGrantValue(value: string): number | null {
  if (!value.startsWith(MERGE_FROM_READ_ACCESS_GRANT_PREFIX)) {
    return null;
  }
  const id = Number.parseInt(value.slice(MERGE_FROM_READ_ACCESS_GRANT_PREFIX.length), 10);
  return Number.isFinite(id) ? id : null;
}

export function readAccessGrantMergeLabel(grant: DocumentReadAccessGrant): string {
  return `${grant.documentName} (${grant.ownerUsername})`;
}

function readAccessGrantOpenRequestUrl(error: unknown): string {
  const ax = error as { config?: { url?: string } };
  return String(ax?.config?.url ?? '');
}

export function isReadAccessGrantOpenRequestError(error: unknown): boolean {
  const url = readAccessGrantOpenRequestUrl(error);
  return url.includes('/documents/read-access/') && url.includes('/open');
}

/** True when a view-only share open failed and the grant should leave "View only shared with me". */
export function shouldRemoveReadAccessGrantAfterOpenError(error: unknown): boolean {
  const ax = error as { response?: { status?: number; data?: unknown } };
  const status = ax?.response?.status;

  if (isReadAccessGrantOpenRequestError(error)) {
    return status != null && status >= 400;
  }

  if (status !== 400 && status !== 403 && status !== 404) {
    return false;
  }

  const msg = formatApiErrorMessage(ax?.response?.data, '').toLowerCase();
  return (
    msg.includes('view-only') ||
    msg.includes('stopped sharing') ||
    msg.includes('no longer available') ||
    msg.includes('no longer exists') ||
    msg.includes('do not have access to this document')
  );
}

/** @deprecated Use shouldRemoveReadAccessGrantAfterOpenError */
export function isReadOnlyShareUnavailableError(error: unknown): boolean {
  return shouldRemoveReadAccessGrantAfterOpenError(error);
}
